import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Avatar} from '@components/common/base/avatar'
import {IconButton} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {TableCell} from '@components/common/base/table/table-cell'
import {Highlight} from '@components/common/highlight/highlight'
import {DivWithHint} from '@components/common/hint/hint'
import {UserName} from '@components/user-name'
import {cn} from '@containers/html-utils'
import {User} from '../../../extensions/user'
import {canSeeEmails, isAdmin, isBlocked} from '../dashboard-utils'
import {UserFields} from '../user-fields'
import {useCompanyUsersStore} from './users-store'
import {TABLE_FIRST_COL_WIDTH} from './users-table'

export const UserItem = observer(
  (props: {user: UserFields; highlight: boolean}) => {
    const {user, highlight} = props
    const [, store] = useCompanyUsersStore()

    return (
      <div onClick={e => store.open(e, user.id)}>
        <div className="group w-full flex h-32 px-16 hover:bg-interactive-l100">
          <Cell minWidth={TABLE_FIRST_COL_WIDTH} className="flex">
            <UserNameAndAvatar user={user} highlight={highlight} />
          </Cell>
          {canSeeEmails() && (
            <Cell>
              <UserEmail user={user} highlight={highlight} />
            </Cell>
          )}
          <Cell>
            <UserPosition user={user} highlight={highlight} />
          </Cell>
          <Cell className={cn('relative')}>
            <UserDepartment user={user} highlight={highlight} />
          </Cell>
        </div>
      </div>
    )
  }
)

const Cell = tw(TableCell)`
  cursor-default
  flex-1
  items-center
  px-16
`

const UserNameAndAvatar = observer(
  ({user, highlight}: {user: UserFields; highlight?: boolean}) => {
    const [state] = useCompanyUsersStore()
    const userIsBlocked = isBlocked(user.id)
    return (
      <TwUser className={cn(userIsBlocked && 'text-disabled')}>
        <Avatar
          userId={user.id}
          size={16}
          className={cn('flex-shrink-0', userIsBlocked && 'opacity-50')}
          hideOutline
          hideStatus
          disabled={userIsBlocked}
        />
        <TwUserName>
          <UserName
            userId={user.id}
            highlight={highlight ? state.filterQuery : undefined}
            className="truncate"
          />
          {user.isAdmin && (
            <DivWithHint content={Lang.msg.permission.isAdmin}>
              <Icon icon="IconCrown" color="warning" />
            </DivWithHint>
          )}
        </TwUserName>
      </TwUser>
    )
  }
)

const UserEmail = observer(
  ({user, highlight}: {user: UserFields; highlight?: boolean}) => {
    const [state] = useCompanyUsersStore()
    const userIsBlocked = isBlocked(user.id)
    return (
      <div className={cn(userIsBlocked && 'text-disabled')}>
        {highlight ? (
          <Highlight highlight={state.filterQuery} text={user.email} />
        ) : (
          <TwBlockedMessage>{user.email}</TwBlockedMessage>
        )}
      </div>
    )
  }
)

const UserPosition = observer(
  ({user, highlight}: {user: UserFields; highlight?: boolean}) => {
    const [state] = useCompanyUsersStore()
    const userIsBlocked = isBlocked(user.id)
    return (
      <div className={cn(userIsBlocked && 'text-disabled')}>
        {highlight ? (
          <Highlight highlight={state.filterQuery} text={user.position} />
        ) : (
          <TwBlockedMessage>{user.position}</TwBlockedMessage>
        )}
      </div>
    )
  }
)

const UserDepartment = observer(
  ({user, highlight}: {user: UserFields; highlight: boolean}) => {
    const [state, store] = useCompanyUsersStore()
    const userIsBlocked = isBlocked(user.id)
    return (
      <>
        <div className="overflow-hidden flex gap-8">
          <div
            className={cn(
              'flex-shrink overflow-hidden text-ellipsis whitespace-nowrap',
              userIsBlocked && 'text-disabled'
            )}
          >
            {highlight ? (
              <Highlight
                highlight={state.filterQuery}
                text={user.departments}
              />
            ) : (
              <div className="truncate leading-5">{user.departments}</div>
            )}
          </div>
          {userIsBlocked ? (
            <TwBlockedMessage className="text-error">
              {Lang.msg.companyDashboard.userIsBlocked}
            </TwBlockedMessage>
          ) : (
            <div className="w-16 flex-shrink-0" />
          )}
        </div>
        {User.canReceiveDirectMessages(user.id) && (highlight || isAdmin()) && (
          <DivWithHint
            onClick={e => store.startChat(e, user.id)}
            content={Lang.msg.openDirectChat}
            className="absolute right-16 hidden group-hover:block"
          >
            <IconButton
              icon="IconMessageDots"
              size={'small'}
              color="secondary"
            />
          </DivWithHint>
        )}
      </>
    )
  }
)

const TwBlockedMessage = tw.div`
  truncate
  leading-5
  flex-shrink-0
`

const TwUser = tw.div`
  overflow-hidden
  flex
  items-center
  gap-8
`

const TwUserName = tw.div`
  truncate
  leading-5
  flex
  items-center
  gap-4
`
